import { useStore } from 'zustand';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave } from '@/ui/services/ServicesContext';
import styles from './MiniGameEntry.module.css';
import { Icon } from './Icon';
import { currentRoom } from '@/ui/util/room';

/**
 * Mini game entry chips, next to the tappable scene objects (dusty chest, packing pile) for players who look
 * for a button: Dusting at Home once the T06 tutorial is done, Packing in the Tiệm once `unl_packing` is open,
 * and Bếp của bà at Home once `unl_cooking` is open (the kitchen counter opens it too, minigames.md §4.2).
 */
export function MiniGameEntry({ game }: { game: Game }) {
  const { t } = useI18n();
  const room = useSave(currentRoom);
  const dustingCleared = useSave((s) => (s.miniGames.mg_dusting?.levelsCleared.length ?? 0) > 0);
  const packingOpen = useSave((s) => s.unlockedIds.includes('unl_packing'));
  const cookingOpen = useSave((s) => s.unlockedIds.includes('unl_cooking'));
  const running = useStore(game.miniGameUi, (s) => s.run !== null || s.intro !== null);
  if (running) return null;
  if (room === 'minimart') {
    if (!packingOpen) return null;
    return (
      <div className={styles.wrap}>
        <button
          type="button"
          className={styles.chip}
          data-testid="mg-packing"
          onClick={() => {
            game.miniGames.openIntro('mg_packing');
          }}
        >
          <Icon name="packing" size={26} />
          {t('ui.hud.packing')}
        </button>
      </div>
    );
  }
  if (room === 'studio' || (!dustingCleared && !cookingOpen)) return null;
  return (
    <div className={styles.wrap}>
      {dustingCleared && (
        <button
          type="button"
          className={styles.chip}
          onClick={() => {
            game.miniGames.openIntro('mg_dusting');
          }}
        >
          <Icon name="dusting" size={26} />
          {t('ui.hud.dusting')}
        </button>
      )}
      {cookingOpen && (
        <button
          type="button"
          className={styles.chip}
          data-testid="mg-cooking"
          onClick={() => {
            game.miniGames.openIntro('mg_cooking');
          }}
        >
          <Icon name="cooking" size={26} />
          {t('ui.hud.cooking')}
        </button>
      )}
    </div>
  );
}
